import {
  FC,
  useRef,
  useEffect,
  memo,
  useCallback,
  useState,
  useMemo
} from 'react';
import {List, ListItem} from '@mui/material';
import {ACCEPTED_TYPES_IN_MESSAGE_LIST} from '@/types/matrix';
import {groupMatrixEventByDate} from '@/components/matrix-chat/utils/helper-functions';

import {useTranslation} from 'react-i18next';
import {SupportedLanguage} from '@/types/common';
import i18n from '@/i18n/i18n';
import {useMatrixContext} from '@/contexts/matrix/client';
import QuotedMessage from '@/components/matrix-chat/message/text-message/user-message/quoted-text-message-chip';

import {useMatrixRoomContext} from '@/contexts/matrix/room';
import DateGroupItem from './date-group-item';
import {useMatrixUnreadEventsContext} from '@/contexts/matrix/unread-events';
import ChatLoading from '@/components/matrix-chat/ui/chat-loading';
import MultimediaPreview from '@/components/matrix-chat/message/multimedia-message/multimedia-preview';
import {isSportjoeDisplayName} from '@/services/matrix/utils';

const EventList: FC = () => {
  const [lastReadEventInList, setLastReadEventInList] = useState<string | null>(
    null
  );
  const [numUnreadEventInList, setNumUnreadEventInList] = useState(0);
  const [isFirstLoad, setIsFirstLoad] = useState(true);
  const [isPaginating, setIsPaginating] = useState(false);
  const eventListRef = useRef<HTMLUListElement | null>(null);
  const {matrixClient, matrixUserId} = useMatrixContext();
  const {lastReadEvent, unreadEventsCount} = useMatrixUnreadEventsContext();

  const {
    readyToHandlePagination,
    matrixEvents,
    loadOlderMatrixEvents,
    matrixEventToQuote,
    lastSentMatrixEvent,
    lastEventSentByUser,
    handleListScrolledToNewestEvent,
    handleSetAllEventsRead,
    isRoomJoined,
    isLoadingRoom,
    finishedSendingImages,
    handleResetSendingImagesState,
    isSendingEvent,
    multimediaEventsToSend,
    multimediaFiles,
    currentMatrixRoomId
  } = useMatrixRoomContext();

  const memberId = matrixUserId;

  const filteredMatrixEvents = matrixEvents.filter((event) => {
    const effectiveEvent = event.getEffectiveEvent();
    const itRelatedToOtherEvent =
      effectiveEvent.content && effectiveEvent.content['m.relates_to'];
    const isAcceptedType = ACCEPTED_TYPES_IN_MESSAGE_LIST.includes(
      effectiveEvent.type
    );
    const sportJoeDisplayName = isSportjoeDisplayName(effectiveEvent);
    return !itRelatedToOtherEvent && isAcceptedType && !sportJoeDisplayName;
  });

  const {t} = useTranslation();
  const defaultLanguage: SupportedLanguage = 'de';
  const currentLanguage: SupportedLanguage =
    (i18n.language as SupportedLanguage) || defaultLanguage;

  const dateGroupedMatrixEvents = groupMatrixEventByDate(
    filteredMatrixEvents,
    t,
    currentLanguage
  );

  const handlePagination = useCallback(async () => {
    setIsPaginating(true);
    const currentScrollTop = eventListRef.current?.scrollTop || 0;
    const currentScrollHeight = eventListRef.current?.scrollHeight || 0;

    try {
      if (matrixClient && currentMatrixRoomId) {
        await loadOlderMatrixEvents();

        setTimeout(() => {
          if (eventListRef.current) {
            const newScrollHeight = eventListRef.current.scrollHeight;
            const heightDifference = newScrollHeight - currentScrollHeight;
            eventListRef.current.scrollTop =
              currentScrollTop + heightDifference;
          }
        }, 0);
      }
    } catch (error) {
      console.error('Failed to load older events:', error);
    } finally {
      setIsPaginating(false);
    }
  }, [currentMatrixRoomId, loadOlderMatrixEvents, matrixClient]);

  // TODO: Improve this, right now doesn't work perfectly with images
  useEffect(() => {
    if (eventListRef.current && isFirstLoad && matrixEvents.length > 0) {
      setNumUnreadEventInList(unreadEventsCount);
      const lastReadIndex = matrixEvents.findIndex(
        (event) => event.getEffectiveEvent().event_id === lastReadEvent
      );

      let newLastReadEventInList = lastReadEvent;

      for (let i = lastReadIndex + 1; i < matrixEvents.length; i++) {
        const event = matrixEvents[i].getEffectiveEvent();
        if (event.sender === memberId) {
          newLastReadEventInList = event.event_id;
        } else if (event.sender !== memberId) {
          break;
        }
      }
      setLastReadEventInList(newLastReadEventInList);

      setTimeout(() => {
        if (eventListRef.current) {
          const firstUnreadEventElement = eventListRef.current.querySelector(
            `[data-event-id="${lastReadEvent}"]`
          ) as HTMLElement;

          if (firstUnreadEventElement && unreadEventsCount > 0) {
            const elementRect = firstUnreadEventElement.getBoundingClientRect();
            const containerRect = eventListRef.current.getBoundingClientRect();

            eventListRef.current.scrollTop =
              elementRect.top -
              containerRect.top +
              eventListRef.current.scrollTop;
          } else {
            eventListRef.current.scrollTop = eventListRef.current.scrollHeight;
          }
        }
      }, 100);

      setIsFirstLoad(false);
    } else if (
      !isFirstLoad &&
      eventListRef.current &&
      !isPaginating &&
      lastSentMatrixEvent &&
      lastSentMatrixEvent.event.type !== 'm.room.redaction' &&
      lastEventSentByUser
    ) {
      eventListRef.current.scrollTop = eventListRef.current.scrollHeight;
      handleListScrolledToNewestEvent();
    } else if (!isFirstLoad && eventListRef.current && matrixEventToQuote) {
      eventListRef.current.scrollTop = eventListRef.current.scrollHeight;
    } else if (
      !isFirstLoad &&
      eventListRef.current &&
      multimediaFiles.length > 0
    ) {
      setTimeout(() => {
        if (eventListRef.current) {
          eventListRef.current.scrollTop = eventListRef.current.scrollHeight;
        }
      }, 200);
    } else if (!isFirstLoad && eventListRef.current && finishedSendingImages) {
      setTimeout(() => {
        if (eventListRef.current) {
          eventListRef.current.scrollTop = eventListRef.current.scrollHeight;
          handleResetSendingImagesState();
        }
      }, 200);
    } else if (
      !isFirstLoad &&
      eventListRef.current &&
      multimediaEventsToSend > 0
    ) {
      setTimeout(() => {
        if (eventListRef.current) {
          eventListRef.current.scrollTop = eventListRef.current.scrollHeight;
        }
      }, 200);
    }
  }, [
    matrixEvents,
    isFirstLoad,
    matrixEventToQuote,
    lastSentMatrixEvent,
    isPaginating,
    lastEventSentByUser,
    handleListScrolledToNewestEvent,
    unreadEventsCount,
    lastReadEvent,
    multimediaFiles,
    memberId,
    finishedSendingImages,
    handleResetSendingImagesState,
    multimediaEventsToSend
  ]);

  useEffect(() => {
    const currentRef = eventListRef.current;

    const handleScroll = (e: Event) => {
      const target = e.target as HTMLUListElement;
      const threshold = 50;
      if (target.scrollTop <= threshold && readyToHandlePagination) {
        handlePagination();
      }
    };

    if (currentRef) {
      currentRef.addEventListener('scroll', handleScroll);

      return () => {
        currentRef.removeEventListener('scroll', handleScroll);
      };
    }
  }, [handlePagination, matrixEvents, readyToHandlePagination]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (eventListRef.current) {
        switch (e.key) {
          case 'ArrowUp':
            eventListRef.current.scrollTop -= 30;
            break;
          case 'ArrowDown':
            eventListRef.current.scrollTop += 30;
            break;
          default:
            break;
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  useEffect(() => {
    if (matrixEvents) {
      handleSetAllEventsRead();
    }
  }, [handleSetAllEventsRead, matrixEvents]);

  // useEffect(() => {
  //   if (matrixEvents) {
  //     const observer = new IntersectionObserver(
  //       (entries) => {
  //         entries.forEach((entry) => {
  //           if (entry.isIntersecting) {
  //             console.log('entry target: ', entry);
  //             const visibleEventId = entry.target.getAttribute('data-event-id');
  //             if (visibleEventId) {
  //               console.log('visible event id: ', visibleEventId);
  //               handleSetEventRead(visibleEventId);
  //             }
  //           }
  //         });
  //       },
  //       { root: eventListRef.current }
  //     );

  //     const messageElements = document.querySelectorAll(
  //       '.matrix-message-element-class'
  //     );
  //     messageElements.forEach((el) => observer.observe(el));

  //     return () => observer.disconnect();
  //   }
  // }, [matrixEvents, handleSetEventRead]);

  useEffect(() => {
    console.log('Matrix Events: ', matrixEvents);
  }, [matrixEvents]);

  const filteredAndGroupedEvents = useMemo(() => {
    return Object.entries(dateGroupedMatrixEvents).map(
      ([dateLabel, dateGroup]) => {
        if (dateGroup.length === 0 || !memberId) {
          return null;
        }

        return (
          <DateGroupItem
            key={dateLabel}
            dateLabel={dateLabel}
            dateGroup={dateGroup}
            memberId={memberId}
            lastReadEventInList={lastReadEventInList}
            numUnreadEventInList={numUnreadEventInList}
          />
        );
      }
    );
  }, [
    dateGroupedMatrixEvents,
    memberId,
    numUnreadEventInList,
    lastReadEventInList
  ]);

  const quotedMessage = useMemo(() => {
    return (
      matrixEventToQuote && (
        <ListItem
          key={`quoted-${matrixEventToQuote.getEffectiveEvent().event_id}`}
          sx={{
            justifyContent: 'flex-start',
            flexDirection: 'column',
            m: 0,
            p: 0,
            mt: 3
          }}
        >
          <QuotedMessage message={matrixEventToQuote} />
        </ListItem>
      )
    );
  }, [matrixEventToQuote]);

  return (
    <List
      sx={{
        maxHeight: {xs: '280px', md: '360px'},
        overflowY: 'auto'
      }}
      ref={eventListRef}
    >
      {isRoomJoined && !isLoadingRoom ? (
        <>
          {filteredAndGroupedEvents}
          {quotedMessage}
          {multimediaFiles && multimediaFiles.length > 0 && !isSendingEvent && (
            <MultimediaPreview multimediaFiles={multimediaFiles} />
          )}
        </>
      ) : (
        <ChatLoading />
      )}
    </List>
  );
};
export default memo(EventList);
